import { Stack, Typography } from '@mui/joy';
import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { NeutralChip, TextEllipsis, TextLine } from 'core/elements';
import { parseString } from 'core/utils';
import {
  ExternalHazardousIcon,
  ExternalMarketingMemo,
  ExternalPartStatus,
  PartImage,
} from 'parts/elements';
import { PartNumberHelper } from 'parts/helpers';
import { IPart } from 'parts/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

interface IProps {
  part: IPart;
  isSelected?: boolean;
  onPartInfo: (part: IPart) => void;
  isAlternateVersionEnabled?: boolean;
}

export const PartCard = memo(
  ({ part, isSelected, onPartInfo, isAlternateVersionEnabled }: IProps) => {
    const { t } = useTranslation();

    return (
      <Stack
        direction={Direction.ROW}
        onClick={() => onPartInfo(part)}
        sx={{
          alignItems: 'flex-start',
          gap: 1.25,
          padding: '12px 16px',
          backgroundColor: isSelected ? 'neutral.100' : 'neutral.50',
          border: '1px solid',
          borderColor: isSelected ? 'primary.400' : 'neutral.300',
          borderRadius: '6px',
          width: '100%',
          cursor: 'pointer',
          transition: 'border-color 0.15s, background-color 0.15s',
          '&:hover': {
            borderColor: isSelected ? 'primary.400' : 'primary.200',
            backgroundColor: isSelected ? 'neutral.50' : 'neutral.100',
          },
        }}
      >
        {part.defaultImage && (
          <PartImage
            url={part.defaultImage.url}
            alt={part.defaultImage.alt}
            sx={{ marginTop: '8px !important' }}
          />
        )}

        <Stack sx={{ flex: 1, gap: 0.5 }}>
          <Stack
            direction={Direction.ROW}
            sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
          >
            <Stack
              direction={Direction.ROW}
              sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center', flex: 1 }}
            >
              {isAlternateVersionEnabled && <ExternalHazardousIcon isHazardous={part.hazardous} />}
              <Typography level={Level.TITLE_MD} color={Color.PRIMARY} sx={{ textWrap: 'wrap' }}>
                {PartNumberHelper.toLabel(part.number)}
              </Typography>
              {part.vendorNumber && (
                <NeutralChip>
                  <Typography level={Level.TITLE_SM}>{part.vendorNumber}</Typography>
                </NeutralChip>
              )}
            </Stack>

            <Button
              variant={Variant.SOFT}
              color={Color.PRIMARY}
              text={isSelected ? t('buttonLabelHide') : t('buttonLabelMoreInfo')}
              startDecorator={<Info />}
              endDecorator={isSelected ? <CaretLeft /> : <CaretRight />}
              onClick={(e) => {
                e.stopPropagation();
                onPartInfo(part);
              }}
              sx={{ minWidth: 130, justifyContent: 'space-between' }}
            />
          </Stack>

          {isAlternateVersionEnabled && (
            <>
              <ExternalPartStatus value={part.externalPartStatus} displayWarning />
              <ExternalMarketingMemo marketingMemo={part.marketingMemo} />
            </>
          )}

          <TextEllipsis
            level={Level.TITLE_SM}
            content={parseString(part.descriptionLong || part.description) as string}
            maxRows={3}
          />

          <TextLine
            color={Color.NEUTRAL}
            level={Level.TITLE_SM}
            text={part.manufacturerName}
            isSubText
            upperCase
          />
        </Stack>
      </Stack>
    );
  }
);

PartCard.displayName = 'PartCard';
